import type { CSSProperties, PointerEvent, ReactNode, Ref } from 'react';

import type { FloorPlanResourceProposal } from '../api/schemas';
import {
  defaultShapeForKind,
  displayRect,
  zoneCategory,
  zoneTone,
  type MarkerRect,
  type MarkerShape,
  type ZoneRect,
} from '../lib/floorPlan';

/** Minimal shape FloorMap needs from a table/marker row. */
export type FloorMapMarker = MarkerRect & {
  id: string;
  name: string;
  seats: number;
  kind: string;
  zone?: string;
  shape?: MarkerShape;
  rotation?: number;
  isPrivate?: boolean;
};

export type FloorMapPlan = { url: string; width: number; height: number } | null;

interface FloorMapProps<T extends FloorMapMarker> {
  canvasRef?: Ref<HTMLDivElement>;
  plan: FloorMapPlan;
  markers: T[];
  /** Ghost proposals drawn read-only, e.g. from floor-plan auto-detection. */
  ghosts?: FloorPlanResourceProposal[];
  /** Open named areas, drawn behind every bookable resource. */
  zones?: ZoneRect[];
  /** The uploaded image is source material; clean mode hides it after detection. */
  showSourceImage?: boolean;
  emptyState?: ReactNode;
  /** Position override while a marker is being dragged (id -> permille x/y). */
  dragPosition?: { id: string; x: number; y: number };
  isSelected?: (marker: T) => boolean;
  /** Extra data-* attrs per marker, e.g. data-private. */
  markerDataAttrs?: (marker: T) => Record<string, boolean | string | undefined>;
  /** Extra content inside a marker button, e.g. an occupant chip. */
  renderMarkerExtra?: (marker: T) => ReactNode;
  /** Semantic activation for markers that open or select something. */
  onMarkerActivate?: (marker: T) => void;
  /** Architecture-only markers can stay visible without becoming focusable controls. */
  isMarkerInteractive?: (marker: T) => boolean;
  onMarkerPointerDown?: (event: PointerEvent<HTMLButtonElement>, marker: T) => void;
  onMarkerPointerMove?: (event: PointerEvent<HTMLButtonElement>, marker: T) => void;
  onMarkerPointerUp?: (marker: T) => void;
  onMarkerPointerCancel?: (marker: T) => void;
}

/** Presentational floor plan: the plan image and one button per marker, positioned by displayRect percentages. */
export function FloorMap<T extends FloorMapMarker>({
  canvasRef,
  plan,
  markers,
  ghosts,
  zones = [],
  showSourceImage = true,
  emptyState,
  dragPosition,
  isSelected,
  markerDataAttrs,
  renderMarkerExtra,
  onMarkerActivate,
  isMarkerInteractive,
  onMarkerPointerDown,
  onMarkerPointerMove,
  onMarkerPointerUp,
  onMarkerPointerCancel,
}: FloorMapProps<T>) {
  const aspectRatio = plan ? `${plan.width} / ${plan.height}` : '4 / 3';

  return (
    <div
      ref={canvasRef}
      className="plan"
      role="application"
      aria-label="Plano del local"
      data-has-image={plan && showSourceImage ? true : undefined}
      data-clean={!showSourceImage || undefined}
      style={{ aspectRatio }}
    >
      {plan && showSourceImage ? <img className="plan-image" src={plan.url} alt="" draggable={false} /> : null}
      {emptyState}
      {zones.map((zone) => (
        <div
          key={`${zone.name}-${zone.x}-${zone.y}`}
          className="plan-zone"
          role="region"
          aria-label={`Zona ${zone.name}`}
          data-tone={zoneTone(zone.name)}
          data-area={zoneCategory(zone.name)}
          style={{
            left: `${zone.x / 10}%`,
            top: `${zone.y / 10}%`,
            width: `${zone.w / 10}%`,
            height: `${zone.h / 10}%`,
          }}
        >
          <span>{zone.name}</span>
        </div>
      ))}
      {markers.map((marker) => {
        const rect = displayRect(marker);
        const dragging = dragPosition?.id === marker.id;
        const x = dragging ? dragPosition.x : rect.gridX;
        const y = dragging ? dragPosition.y : rect.gridY;
        const interactive = isMarkerInteractive?.(marker) ?? true;
        const style = {
          left: `${x / 10}%`,
          top: `${y / 10}%`,
          width: `${rect.gridW / 10}%`,
          height: `${rect.gridH / 10}%`,
          '--marker-rotation': `${marker.rotation ?? 0}deg`,
        } as CSSProperties;
        const content = (
          <>
            <span className="plan-marker-surface" aria-hidden="true" />
            <span className="plan-marker-name">{marker.name}</span>
            {marker.kind !== 'bar' ? <span className="plan-marker-seats">{marker.seats}p</span> : null}
            {renderMarkerExtra?.(marker)}
          </>
        );

        if (!interactive) {
          return (
            <div
              key={marker.id}
              className="plan-marker"
              role="img"
              data-kind={marker.kind}
              data-shape={marker.shape ?? defaultShapeForKind(marker.kind)}
              data-tone={zoneTone(marker.zone)}
              data-area={marker.kind === 'bar' ? 'bar' : zoneCategory(marker.zone)}
              data-interactive="false"
              {...markerDataAttrs?.(marker)}
              style={style}
              aria-label={markerAriaLabel(marker)}
            >
              {content}
            </div>
          );
        }

        return (
          <button
            key={marker.id}
            type="button"
            className="plan-marker"
            data-kind={marker.kind}
            data-shape={marker.shape ?? defaultShapeForKind(marker.kind)}
            data-tone={zoneTone(marker.zone)}
            data-area={marker.kind === 'bar' ? 'bar' : zoneCategory(marker.zone)}
            data-selected={isSelected?.(marker) || undefined}
            data-dragging={dragging || undefined}
            {...markerDataAttrs?.(marker)}
            style={style}
            aria-label={markerAriaLabel(marker)}
            onClick={() => onMarkerActivate?.(marker)}
            onPointerDown={(event) => onMarkerPointerDown?.(event, marker)}
            onPointerMove={(event) => onMarkerPointerMove?.(event, marker)}
            onPointerUp={() => onMarkerPointerUp?.(marker)}
            onPointerCancel={() => onMarkerPointerCancel?.(marker)}
          >
            {content}
          </button>
        );
      })}
      {ghosts?.map((ghost, index) => (
        <div
          key={`ghost-${index}`}
          className="plan-marker plan-ghost"
          role="img"
          aria-label={markerAriaLabel(ghost)}
          data-kind={ghost.kind}
          data-private={ghost.isPrivate || undefined}
          data-shape={ghost.shape}
          data-tone={zoneTone(ghost.zone)}
          data-area={ghost.kind === 'bar' ? 'bar' : zoneCategory(ghost.zone)}
          style={{
            left: `${ghost.x / 10}%`,
            top: `${ghost.y / 10}%`,
            width: `${ghost.w / 10}%`,
            height: `${ghost.h / 10}%`,
            '--marker-rotation': `${ghost.rotation}deg`,
          } as CSSProperties}
        >
          <span className="plan-marker-surface" aria-hidden="true" />
          <span className="plan-marker-name">{ghost.name}</span>
          {ghost.kind !== 'bar' ? <span className="plan-marker-seats">{ghost.seats}p</span> : null}
        </div>
      ))}
    </div>
  );
}

function markerAriaLabel(marker: { name: string; seats: number; kind: string; isPrivate?: boolean }): string {
  if (marker.kind === 'room') {
    return `${marker.name}, ${marker.isPrivate ? 'salón privado' : 'salón'} para ${marker.seats} personas`;
  }
  if (marker.kind === 'bar') return `${marker.name}, área de bar no reservable`;
  return `${marker.name}, ${marker.seats} personas`;
}
